import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ApiError, fetchDocument, fetchDocumentLink } from "../lib/api";
import { useCan } from "../lib/session";

const MAX_PAGE_PARAM = 100_000;

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function parseDate(value: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(value: string | null): string {
  const parsed = parseDate(value);
  return parsed ? dateFormatter.format(parsed) : value || "Date unavailable";
}

function formatLabel(value: string | null, fallback: string): string {
  if (!value) return fallback;
  const normalized = value.replace(/[_-]+/g, " ").trim();
  return normalized
    ? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
    : fallback;
}

function strictPage(value: string | null): number | null {
  if (!value || !/^[1-9]\d*$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed <= MAX_PAGE_PARAM ? parsed : null;
}

function pdfUrlAtPage(url: string, page: number): string {
  const target = new URL(url, window.location.href);
  target.hash = `page=${page}`;
  return target.toString();
}

function ViewerState({
  title,
  detail,
  tone = "neutral",
  onRetry,
  retryLabel = "Retry PDF preview",
}: {
  title: string;
  detail?: string;
  tone?: "neutral" | "error";
  onRetry?: () => void;
  retryLabel?: string;
}) {
  return (
    <div
      className={`document-pdf-state document-pdf-state--${tone}`}
      role={tone === "error" ? "alert" : "status"}
    >
      <div>
        <strong>{title}</strong>
        {detail ? <span>{detail}</span> : null}
      </div>
      {onRetry ? (
        <button className="btn" type="button" onClick={onRetry} aria-label={retryLabel}>
          Retry
        </button>
      ) : null}
    </div>
  );
}

function linkErrorCopy(error: unknown): { title: string; detail: string } {
  if (error instanceof ApiError && error.status === 403) {
    return {
      title: "PDF viewing is not permitted",
      detail: "Your access or this document's licence does not permit original-file viewing.",
    };
  }
  if (error instanceof ApiError && error.status === 422) {
    return {
      title: "Original PDF unavailable",
      detail: "Athena does not have an original file stored for this document.",
    };
  }
  return {
    title: "PDF could not be loaded",
    detail: "The document record is available, but the original-file link could not be created.",
  };
}

export default function Document() {
  const { documentId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const canExport = useCan("research.export");
  const rawPage = searchParams.get("page");
  const explicitPage = strictPage(rawPage);
  const [manualPending, setManualPending] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);

  const doc = useQuery({
    queryKey: ["document", documentId],
    queryFn: () => fetchDocument(documentId),
    enabled: Boolean(documentId),
    retry: false,
  });
  const document = doc.data?.document;
  const pageCount = document?.page_count ?? null;
  const requestedPage = explicitPage ?? 1;
  const currentPage = Math.max(
    1,
    pageCount && pageCount > 0 ? Math.min(requestedPage, pageCount) : requestedPage,
  );

  const original = useQuery({
    queryKey: ["document", documentId, "original-link"],
    queryFn: () => fetchDocumentLink(documentId),
    enabled: Boolean(documentId && doc.isSuccess && document?.is_pdf && canExport),
    retry: false,
    staleTime: 55 * 60 * 1000,
  });

  useEffect(() => {
    if (!doc.isSuccess || !rawPage || rawPage === String(currentPage)) return;
    const next = new URLSearchParams(searchParams);
    next.set("page", String(currentPage));
    setSearchParams(next, { replace: true });
  }, [currentPage, doc.isSuccess, rawPage, searchParams, setSearchParams]);

  const pdfSrc = useMemo(() => {
    if (!original.data?.document.is_pdf || !original.data.link.url) return null;
    return pdfUrlAtPage(original.data.link.url, currentPage);
  }, [currentPage, original.data]);

  async function openNonPdfOriginal() {
    const target = window.open("about:blank", "_blank");
    if (!target) {
      setManualError("Your browser blocked the new tab. Allow pop-ups and try again.");
      return;
    }
    target.opener = null;
    setManualPending(true);
    setManualError(null);
    try {
      const response = await fetchDocumentLink(documentId);
      target.location.replace(response.link.url);
    } catch (error) {
      target.close();
      setManualError(linkErrorCopy(error).detail);
    } finally {
      setManualPending(false);
    }
  }

  if (doc.isLoading) {
    return (
      <div className="document-pdf-page-state">
        <ViewerState title="Loading document" detail="Preparing the original-file viewer…" />
      </div>
    );
  }

  if (doc.isError) {
    const notFound = doc.error instanceof ApiError && doc.error.status === 404;
    return (
      <div className="document-pdf-page-state">
        <ViewerState
          title={notFound ? "Document not found" : "Document could not be loaded"}
          detail={
            notFound
              ? "It may have been removed or the link may be out of date."
              : "Retry the document request or return to the catalog."
          }
          tone="error"
          onRetry={notFound ? undefined : () => void doc.refetch()}
          retryLabel="Retry document"
        />
        <Link className="document-pdf-return" to="/documents">
          Return to Documents
        </Link>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="document-pdf-page-state">
        <ViewerState
          title="Document not found"
          detail="It may have been removed or the link may be out of date."
          tone="error"
        />
        <Link className="document-pdf-return" to="/documents">
          Return to Documents
        </Link>
      </div>
    );
  }

  const title = document.title ?? document.external_document_id ?? "Untitled document";
  const source = document.source_name || document.provider || document.source_type;
  const originalError = original.isError ? linkErrorCopy(original.error) : null;

  return (
    <div className="document-pdf-page">
      <header className="document-pdf-head">
        <div className="document-pdf-heading">
          <nav className="document-pdf-breadcrumb" aria-label="Breadcrumb">
            <Link to="/documents">Documents</Link>
            <span aria-hidden="true">/</span>
            <span>Viewer</span>
          </nav>
          <div className="document-pdf-eyebrow">
            {document.ticker || "No ticker"} · {formatLabel(source, "Source unavailable")}
          </div>
          <h1>{title}</h1>
          <div className="document-pdf-summary">
            <span>{document.company_name || "Company unavailable"}</span>
            <time dateTime={document.doc_date ?? undefined}>{formatDate(document.doc_date)}</time>
            {pageCount ? <span>{pageCount} pages</span> : null}
          </div>
        </div>

        {pdfSrc ? (
          <div className="document-pdf-actions">
            <button
              className="btn"
              type="button"
              disabled={original.isFetching}
              onClick={() => void original.refetch()}
            >
              {original.isFetching ? "Refreshing…" : "Reload preview"}
            </button>
            <a className="btn" href={pdfSrc} target="_blank" rel="noopener noreferrer">
              Open in new tab ↗
            </a>
          </div>
        ) : null}
      </header>

      <section
        className="document-pdf-surface"
        aria-label="PDF viewer"
        aria-busy={original.isLoading || original.isFetching}
      >
        {!document.is_pdf ? (
          <div className="document-pdf-fallback">
            <ViewerState
              title="Inline preview unavailable"
              detail={`This ${formatLabel(document.content_type, "file type")} opens in a separate tab.`}
            />
            {canExport ? (
              <button
                className="btn"
                type="button"
                onClick={() => void openNonPdfOriginal()}
                disabled={manualPending}
              >
                {manualPending ? "Opening…" : "Open original ↗"}
              </button>
            ) : null}
            {manualError ? (
              <div className="document-pdf-inline-error" role="alert">
                {manualError}
              </div>
            ) : null}
          </div>
        ) : !canExport ? (
          <ViewerState
            title="Original PDF unavailable"
            detail="Your current access does not include original-file viewing."
            tone="error"
          />
        ) : original.isLoading || !original.isFetched ? (
          <ViewerState title="Loading PDF" detail="Creating a secure inline viewing link…" />
        ) : originalError ? (
          <ViewerState
            title={originalError.title}
            detail={originalError.detail}
            tone="error"
            onRetry={() => void original.refetch()}
          />
        ) : pdfSrc ? (
          <iframe
            className="document-pdf-frame"
            title={`${title} PDF`}
            src={pdfSrc}
            referrerPolicy="no-referrer"
          />
        ) : (
          <ViewerState
            title="Inline preview unavailable"
            detail="The stored original is not a PDF."
            tone="error"
          />
        )}
      </section>
    </div>
  );
}
